import React, { useState, ChangeEvent, FormEvent, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { FaRegCircleCheck } from "react-icons/fa6";
import strings from "../../constants/strings";
import logo from "../../assets/images/logo.png";

interface FormData {
  password: string;
  confirmPassword: string;
}

const ResetPasswordScreen = () => {
  const [formData, setFormData] = useState<FormData>({
    password: "",
    confirmPassword: ""
  });

  const [errors, setErrors] = useState<Partial<FormData>>({});

  const [generalBanner, setGeneralBanner] = useState("");
  const [isError, setIsError] = useState(true);

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.id]: e.target.value });
    setErrors({ ...errors, [e.target.id]: "" });
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<FormData> = {};

    if (!formData.password.trim()) {
      newErrors.password = strings.errorPassword;
    }

    if (!formData.confirmPassword.trim()) {
      newErrors.confirmPassword = strings.errorPassword;
    }

    if (
      formData.password.trim() &&
      formData.confirmPassword.trim() &&
      formData.password !== formData.confirmPassword
    ) {
      newErrors.confirmPassword = strings.errorPasswordMismatch;
    }

    setErrors(newErrors);

    const errorCount = Object.keys(newErrors).length;
    if (errorCount > 0) {
      setGeneralBanner(`${strings.fixAllErrors} ${errorCount}`);
      setIsError(true);
      return false;
    }

    setGeneralBanner("");
    return true;
  };

  const location = useLocation();
  const [resetId, setResetId] = useState("");
  const [username, setUsername] = useState("");

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const resetIdFromUrl = params.get("resetId") || "";
    const usernameFromUrl = params.get("username") || "";

    setResetId(resetIdFromUrl);
    setUsername(usernameFromUrl);
  }, [location]);


  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      const response = await fetch("https://dev-admin.ptlytics.ca/auth/confirm-reset-password", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          resetId,
          username, 
          password: formData.password,
          confirmPassword: formData.confirmPassword
        })
      });
      if (!response.ok) {
        setGeneralBanner(strings.errorEmail);
        setIsError(true);
      } else {
        setGeneralBanner(strings.successPassword);
        setIsError(false);
        setFormData({
          password: "",
          confirmPassword: ""
        });

        setTimeout(() => {
          setGeneralBanner("");
        }, 3000);
      }
    } catch (error) {
      setGeneralBanner(`${strings.errorSubmitting} ${error}`);
      setIsError(true);
    }
  };

  return (
    <div className="min-h-screen w-full overflow-y-auto bg-home-background bg-cover bg-center grid grid-cols-1 lg:grid-cols-2 lg:gap-x-6 scrollable-container">
      <div className="flex flex-col items-center lg:items-start justify-center text-white pt-10 px-10 lg:pl-48 lg:pr-20">
        <img src={logo} alt="PTLytics Logo" className="w-[193px] h-[60px] mb-4" />
        <p className="text-[22px] lg:text-[64px] text-center lg:text-start font-urbanist font-bold">
          {strings.bannerContactUs}
        </p>
      </div>
      <div className="flex items-center justify-center lg:justify-start p-6 min-h-screen scrollable-container">
        <div className="bg-white rounded-md shadow-md p-8 w-full max-w-[488px] h-auto">
          <h2 className="text-[24px] lg:text-[28px] font-semibold mb-2 font-urbanist">
            {strings.resetPassword}
          </h2>
          <p className="text-custom-gray mb-6 font-normal">
            {strings.resetPasswordSubtitle}
          </p>

          {generalBanner && (
            <div
              className={`px-4 py-3 mb-4 border-b-2 ${isError
                ? "bg-custom-light-red border-custom-dark-red text-custom-dark-red"
                : "bg-custom-light-green border-custom-dark-green text-custom-dark-green"
                }`}
            >
              <strong className="flex">
                {isError ? (
                  <span className="mr-2 font-bold text-2xl self-start">&#9888;</span>
                ) : (
                  <FaRegCircleCheck className="mr-2 text-custom-dark-green self-center font-bold" />
                )}
                <span className="self-center">{generalBanner}</span>
              </strong>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className={`border ${errors.password ? 'border-custom-dark-red border-2 mb-2' : 'border-gray-300 mb-4'} rounded-md p-3`}>
              <label htmlFor="name" className="block text-custom-gray font-normal text-sm mb-1">
                {strings.password}
              </label>
              <input
                id="password"
                type="text"
                placeholder={strings.passwordPlaceholder}
                className="w-full border-none outline-none placeholder:text-custom-gray text-base"
                value={formData.password}
                onChange={handleChange}
              />
            </div>
            {errors.password && (
              <p className="text-custom-red text-sm mt-1 flex items-center">
                <span className="mr-1 font-bold text-xl">&#9888;</span> {errors.password}
              </p>
            )}

            <div className={`border ${errors.confirmPassword ? 'border-custom-dark-red border-2 my-2' : 'border-gray-300 my-4'} rounded-md p-3`}>
              <label htmlFor="confirmPassword" className="block text-custom-gray font-normal text-sm mb-1">
                {strings.confirmPassword}
              </label>
              <input
                id="confirmPassword"
                type="text"
                placeholder={strings.passwordPlaceholder}
                className="w-full border-none outline-none placeholder:text-custom-gray text-base"
                value={formData.confirmPassword}
                onChange={handleChange}
              />
            </div>
            {errors.confirmPassword && (
              <p className="text-custom-red text-sm mt-1 flex items-center">
                <span className="mr-1 font-bold text-xl">&#9888;</span> {errors.confirmPassword}
              </p>
            )}

            
            <button
              type="submit"
              className="bg-custom-blue text-white mt-4 px-4 py-2 rounded-full w-full font-bold"
            >
              {strings.resetPasswordBtn}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordScreen;
